import { useEffect, useMemo, useRef, useState } from "react";

import { getAuthConfig } from "./api";
import {
  STATUS_OPTIONS,
  TOKEN_KEY,
  initialAdminClient,
  initialAdminDeveloper,
  initialForgotPassword,
  initialLogin,
  initialResetPassword,
  initialTicket,
} from "./constants";
import { getPasswordChecks } from "./password";

export function useSupportPortalState() {
  const [mode, setMode] = useState("login");
  const [activeView, setActiveView] = useState("tickets");
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [user, setUser] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [adminClients, setAdminClients] = useState([]);
  const [adminDevelopers, setAdminDevelopers] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState("");
  const selectedTicketIdRef = useRef("");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [loginForm, setLoginForm] = useState(initialLogin);
  const [adminClientForm, setAdminClientForm] = useState(initialAdminClient);
  const [adminDeveloperForm, setAdminDeveloperForm] = useState(initialAdminDeveloper);
  const [forgotPasswordForm, setForgotPasswordForm] = useState(initialForgotPassword);
  const [resetPasswordForm, setResetPasswordForm] = useState(initialResetPassword);
  const [ticketForm, setTicketForm] = useState(initialTicket);
  const [ticketFiles, setTicketFiles] = useState([]);
  const [messageDraft, setMessageDraft] = useState("");
  const [messageFiles, setMessageFiles] = useState([]);
  const [adminStatus, setAdminStatus] = useState("ticket_ouvert");
  const [adminComment, setAdminComment] = useState("");
  const [developmentTimeMinutes, setDevelopmentTimeMinutes] = useState("");
  const [ticketDisplayMode, setTicketDisplayMode] = useState("list");
  const [isDeleteClientDialogOpen, setIsDeleteClientDialogOpen] = useState(false);
  const [isDeleteTicketDialogOpen, setIsDeleteTicketDialogOpen] = useState(false);
  const [isTmaPackageDialogOpen, setIsTmaPackageDialogOpen] = useState(false);
  const [isClientEditorOpen, setIsClientEditorOpen] = useState(false);
  const [isDeveloperEditorOpen, setIsDeveloperEditorOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const [clientStatusFilter, setClientStatusFilter] = useState("all");
  const [tmaPackageForm, setTmaPackageForm] = useState({
    label: "TMA Juillet 2026",
    minutes: "",
  });
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showAdminClientPassword, setShowAdminClientPassword] = useState(false);
  const [showAdminDeveloperPassword, setShowAdminDeveloperPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showResetPasswordConfirm, setShowResetPasswordConfirm] = useState(false);

  const isEditingClient = Boolean(adminClientForm.id);
  const isEditingDeveloper = Boolean(adminDeveloperForm.id);
  const selectedAdminClient = useMemo(
    () => adminClients.find((client) => client.id === adminClientForm.id) || null,
    [adminClients, adminClientForm.id]
  );
  const filteredAdminClients = useMemo(() => {
    const search = clientSearch.trim().toLowerCase();
    return adminClients.filter((client) => {
      const matchesSearch =
        !search ||
        client.full_name.toLowerCase().includes(search) ||
        client.email.toLowerCase().includes(search);
      const matchesStatus =
        clientStatusFilter === "all" ||
        (clientStatusFilter === "active" && client.is_active) ||
        (clientStatusFilter === "inactive" && !client.is_active);
      return matchesSearch && matchesStatus;
    });
  }, [adminClients, clientSearch, clientStatusFilter]);
  const activeDevelopers = useMemo(
    () => adminDevelopers.filter((developer) => developer.is_active && developer.can_access_platform),
    [adminDevelopers]
  );
  const resetPasswordChecks = useMemo(
    () => getPasswordChecks(resetPasswordForm.new_password, resetPasswordForm.confirm_password, true),
    [resetPasswordForm.new_password, resetPasswordForm.confirm_password]
  );
  const isResetPasswordFormValid = useMemo(
    () => Boolean(resetPasswordForm.token) && resetPasswordChecks.every((check) => check.valid),
    [resetPasswordChecks, resetPasswordForm.token]
  );
  const authHeaders = useMemo(() => getAuthConfig(token), [token]);

  const updateSelectedTicketId = (ticketId) => {
    selectedTicketIdRef.current = ticketId;
    setSelectedTicketId(ticketId);
  };

  const filteredTickets = useMemo(() => {
    if (!selectedClientId) return tickets;
    return tickets.filter((ticket) => ticket.client_id === selectedClientId);
  }, [tickets, selectedClientId]);

  const kanbanColumns = useMemo(
    () =>
      STATUS_OPTIONS.map((statusOption) => ({
        ...statusOption,
        tickets: filteredTickets.filter((ticket) => ticket.status === statusOption.value),
      })),
    [filteredTickets]
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const routeMode = params.get("mode");
    const resetToken = params.get("token");

    if (routeMode === "reset") {
      setMode("reset");
      setResetPasswordForm((current) => ({
        ...current,
        token: resetToken || "",
      }));
    }
  }, []);

  return {
    activeDevelopers,
    activeView,
    adminClientForm,
    adminClients,
    adminComment,
    adminDeveloperForm,
    adminDevelopers,
    adminStatus,
    authHeaders,
    clientSearch,
    clientStatusFilter,
    developmentTimeMinutes,
    filteredAdminClients,
    filteredTickets,
    forgotPasswordForm,
    isClientEditorOpen,
    isDeleteClientDialogOpen,
    isDeleteTicketDialogOpen,
    isDeveloperEditorOpen,
    isEditingClient,
    isEditingDeveloper,
    isResetPasswordFormValid,
    isTmaPackageDialogOpen,
    kanbanColumns,
    loading,
    loadingData,
    loginForm,
    messageDraft,
    messageFiles,
    mode,
    notifications,
    resetPasswordForm,
    selectedAdminClient,
    selectedClientId,
    selectedTicket,
    selectedTicketId,
    selectedTicketIdRef,
    setActiveView,
    setAdminClientForm,
    setAdminClients,
    setAdminComment,
    setAdminDeveloperForm,
    setAdminDevelopers,
    setAdminStatus,
    setClientSearch,
    setClientStatusFilter,
    setDevelopmentTimeMinutes,
    setForgotPasswordForm,
    setIsClientEditorOpen,
    setIsDeleteClientDialogOpen,
    setIsDeleteTicketDialogOpen,
    setIsDeveloperEditorOpen,
    setIsTmaPackageDialogOpen,
    setLoading,
    setLoadingData,
    setLoginForm,
    setMessageDraft,
    setMessageFiles,
    setMode,
    setNotifications,
    setResetPasswordForm,
    setSelectedClientId,
    setSelectedTicket,
    setShowAdminClientPassword,
    setShowAdminDeveloperPassword,
    setShowLoginPassword,
    setShowResetPassword,
    setShowResetPasswordConfirm,
    setTicketDisplayMode,
    setTicketFiles,
    setTicketForm,
    setTickets,
    setTmaPackageForm,
    setToken,
    setUnreadCount,
    setUser,
    showAdminClientPassword,
    showAdminDeveloperPassword,
    showLoginPassword,
    showResetPassword,
    showResetPasswordConfirm,
    ticketDisplayMode,
    ticketFiles,
    ticketForm,
    tickets,
    tmaPackageForm,
    token,
    unreadCount,
    updateSelectedTicketId,
    user,
  };
}
